import { ChevronLeftIcon, CubeIcon, PlusIcon, SparklesIcon, Squares2X2Icon } from '@heroicons/react/24/outline';
import DoorIcon from '../../assets/icons/door.svg?react';
import WindowIcon from '../../assets/icons/window.svg?react';
import RulerIcon from '../../assets/icons/ruler.svg?react';
import classNames from 'classnames';
import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import {
  createEmptyDimensionSpec,
  createEmptyObjectDefinitionSpec,
  createEmptyOpeningSpec,
  DATA_BUILTIN_DIMENSION_SPECS,
  DATA_BUILTIN_DOOR_SPECS,
  DATA_BUILTIN_WINDOW_SPECS,
  DIMENSION_CATEGORY_ID,
  OPENING_CATEGORY_IDS,
  parseObjectDefinitionSpec,
  type ObjectDefinitionSpec,
  type ObjectOpeningKind,
} from 'shared-types';
import { Button } from '../../components/Button';
import { Input } from '../../components/inputs/Input';
import { TextArea } from '../../components/inputs/TextArea';
import { Option, Select } from '../../components/inputs/Select';
import { Modal } from '../../components/Modal';
import { evaluateCustomGlyph } from '../../potree/sketchObjects/custom/evaluateCustomGlyph';
import { OBJECT_DEFINITION_TEMPLATES } from '../../potree/sketchObjects/custom/templates';
import { OPENING_CATEGORY_LABELS } from '../../potree/sketchObjects/custom/openings/builtinOpenings';
import { evaluateCustomOpeningLocal } from '../../potree/sketchObjects/custom/openings/evaluateCustomOpening';
import { DIMENSION_CATEGORY_LABEL, SketchObjectCategory } from '../../potree/sketchObjects/types';
import { getSketchObjectType } from '../../potree/sketchObjects/registry';
import { t } from '../../potree/translation/markers';
import { T, useT } from '../../translation/src';
import { ObjectDefinitionKind, useObjectDefinitionCreateMutation, useObjectDefinitionGenerateMutation, type ObjectLibraryFieldsFragment } from '../../types/graphqlTypes';
import { AiErrorMessage } from '../objectEditor/ai/AiErrorMessage';
import { AiMessageLog } from '../objectEditor/ai/AiMessageLog';
import { AiProgress } from '../objectEditor/ai/AiProgress';
import { AiProgressTracker, useAiRunState } from '../objectEditor/ai/aiProgressTracker';
import { AI_MAX_PROMPT_LENGTH, aiErrorCode, type AiErrorCode } from '../objectEditor/ai/aiShared';
import { ReferenceImageField, useReferenceImage } from '../objectEditor/ai/ReferenceImageField';
import { libraryCategoryOptions } from './categories';
import { isSmartSpec } from './smart';
import { GlyphPreview } from './GlyphPreview';
import { TranslatedLabel } from './TranslatedLabel';

const BLANK = 'blank';

/** How the new object starts: a blank drawing / template, or designed by the AI from a description. */
type StartMode = 'template' | 'describe';

interface Starter {
  id: string;
  label: string;
  description: string;
  category: string;
  spec: ObjectDefinitionSpec;
}

/** Default-size glyph of a spec (template tiles). */
const previewSegments = (spec: ObjectDefinitionSpec): number[] =>
  spec.opening
    ? evaluateCustomOpeningLocal(spec, { width: spec.defaultSize.width })
    : evaluateCustomGlyph(spec, { center: { x: 0, y: 0 }, dir: { x: 1, y: 0 }, norm: { x: 0, y: 1 }, width: spec.defaultSize.width, depth: spec.defaultSize.depth, z: 0 }, {});

/** What is being made: a placeable object, a door / window symbol or a dimension (both in a fixed category). */
type NewKind = 'OBJECT' | ObjectOpeningKind | 'DIMENSION';

/** The kinds the AI designs (dimensions are drawn from a style). */
type AiKind = Exclude<NewKind, 'DIMENSION'>;

const AI_KIND: Record<AiKind, ObjectDefinitionKind> = { OBJECT: ObjectDefinitionKind.Object, DOOR: ObjectDefinitionKind.Door, WINDOW: ObjectDefinitionKind.Window };

/** Starting points of a new dimension: the measured line alone, or a built-in style. */
function dimensionStarters(): Starter[] {
  const blank: Starter = { id: BLANK, label: t('Blank'), description: t('Start from the dimension line alone'), category: DIMENSION_CATEGORY_ID, spec: createEmptyDimensionSpec() };
  return [
    blank,
    ...DATA_BUILTIN_DIMENSION_SPECS.map(({ id, label, spec }) => ({
      id,
      label: getSketchObjectType(id)?.label ?? label,
      description: t('Ends, line style and extension lines are options'),
      category: DIMENSION_CATEGORY_ID,
      spec,
    })),
  ];
}

/** Starting points of a new door / window: blank, or a built-in symbol's data twin. */
function openingStarters(kind: ObjectOpeningKind): Starter[] {
  const category = OPENING_CATEGORY_IDS[kind];
  const blank: Starter = { id: BLANK, label: t('Blank'), description: t('Start from an empty drawing'), category, spec: createEmptyOpeningSpec(kind) };
  if (kind === 'WINDOW') return [blank, { id: 'window_standard', label: t('Standard window'), description: t('Double glass line, optional roller shutter'), category, spec: DATA_BUILTIN_WINDOW_SPECS['STANDARD'] }];
  return [
    blank,
    { id: 'door_single', label: t('Single door'), description: t('One leaf with its swing; scales with the door width'), category, spec: DATA_BUILTIN_DOOR_SPECS['SINGLE'] },
    { id: 'door_double', label: t('Double door'), description: t('Two leaves meeting in the middle'), category, spec: DATA_BUILTIN_DOOR_SPECS['DOUBLE'] },
    { id: 'door_bifold', label: t('Bifold door'), description: t('Two folding halves'), category, spec: DATA_BUILTIN_DOOR_SPECS['BIFOLD'] },
  ];
}

interface NewObjectModalProps {
  open: boolean;
  onClose: () => void;
  organisationId: string;
  config: ObjectLibraryFieldsFragment['config'] | undefined;
  onCreated: (definitionId: string) => void;
}

/** Name, category and a starting point (blank, a template, or "describe it": designed by the AI) for a new custom object. */
const NewObjectModal_: React.FC<NewObjectModalProps> = ({ open, onClose, organisationId, config, onCreated }) => {
  const [kind, setKind] = useState<NewKind>('OBJECT');
  const [aiError, setAiError] = useState<AiErrorCode | null>(null);
  /** First what is being made (object, door, window), then its details. */
  const [step, setStep] = useState<'kind' | 'details'>('kind');
  // Every opening of the dialog starts at the first step.
  const [wasOpen, setWasOpen] = useState(open);
  if (open !== wasOpen) {
    setWasOpen(open);
    if (open) setStep('kind');
  }
  // Parametric starting points are smart objects: those are made with the AI, not offered as templates.
  const starters = useMemo<Starter[]>(
    () =>
      (kind === 'OBJECT'
        ? [
            { id: BLANK, label: t('Blank'), description: t('Start from an empty drawing'), category: SketchObjectCategory.FURNITURE, spec: createEmptyObjectDefinitionSpec() },
            ...OBJECT_DEFINITION_TEMPLATES.map((template) => ({ id: template.id, label: template.label, description: template.description, category: template.category, spec: template.spec })),
          ]
        : kind === 'DIMENSION'
          ? dimensionStarters()
          : openingStarters(kind)
      ).filter((starter) => !isSmartSpec(starter.spec)),
    [kind],
  );
  const previews = useMemo(() => new Map(starters.map((s) => [s.id, previewSegments(s.spec)])), [starters]);
  const categories = useMemo(() => libraryCategoryOptions(config), [config]);

  const [starterId, setStarterId] = useState(BLANK);
  const [name, setName] = useState('');
  const [categoryId, setCategoryId] = useState<string>(SketchObjectCategory.FURNITURE);
  const [createDefinition, { loading: creating, error }] = useObjectDefinitionCreateMutation();
  const namePlaceholder = useT('e.g. wardrobe with sliding doors', { swc: true });
  const backLabel = useT('back', { swc: true });
  const KIND_CHOICES: ReadonlyArray<{ kind: NewKind; icon: typeof CubeIcon | typeof DoorIcon; label: string; description: string }> = [
    { kind: 'OBJECT', icon: CubeIcon, label: useT('object', { swc: true }), description: useT('furniture, fixtures, stairs, … placed anywhere in the design', { swc: true }) },
    { kind: 'DOOR', icon: DoorIcon, label: useT('door', { swc: true }), description: useT('a door symbol, offered in the door type of doors', { swc: true }) },
    { kind: 'WINDOW', icon: WindowIcon, label: useT('window', { swc: true }), description: useT('a window symbol, offered in the window type of windows', { swc: true }) },
    { kind: 'DIMENSION', icon: RulerIcon, label: useT('dimension', { swc: true }), description: useT('a dimension style, offered in the dimension tool of the design tab', { swc: true }) },
  ];

  // Smart objects (designed by the AI) come first.
  const [mode, setMode] = useState<StartMode>('describe');
  const [description, setDescription] = useState('');
  const reference = useReferenceImage();
  const [generateDefinition, { loading: generating }] = useObjectDefinitionGenerateMutation();
  // Live progress of the generation (Pusher); stops following when the modal goes away.
  const [progress] = useState(() => new AiProgressTracker());
  const run = useAiRunState(progress);
  useEffect(() => () => progress.end(), [progress]);
  const descriptionPlaceholders: Record<AiKind, string> = {
    OBJECT: useT('e.g. a 3-seat sofa with armrests; seat count adjustable', { swc: true }),
    DOOR: useT('e.g. a pocket door that slides into the wall', { swc: true }),
    WINDOW: useT('e.g. a tilt-turn window with two sashes and a sill', { swc: true }),
  };
  const loading = creating || generating || run.running;

  const starter = starters.find((s) => s.id === starterId) ?? starters[0];

  const pickStarter = useCallback((next: Starter) => {
    setStarterId(next.id);
    setCategoryId(next.category);
  }, []);

  const create = useCallback(async () => {
    const trimmed = name.trim();
    if (!trimmed) return;
    try {
      const result = await createDefinition({
        variables: { organisationId, name: trimmed, categoryId, spec: starter.spec, templateId: starter.id === BLANK ? null : starter.id },
      });
      const id = result.data?.objectDefinitionCreate.id;
      if (id) onCreated(id);
    } catch {
      /* shown inline from the mutation's error state */
    }
  }, [name, createDefinition, organisationId, categoryId, starter, onCreated]);

  /** Describe it: the AI designs the spec, then it is created like any other start (the name is optional here). */
  const generateAndCreate = useCallback(async () => {
    const prompt = description.trim();
    if (!prompt || kind === 'DIMENSION') return;
    setAiError(null);
    let spec: ObjectDefinitionSpec;
    let suggestedName: string | null;
    try {
      const progressChannel = await progress.begin();
      // Doors / windows get the AI's door / window instructions and always come back as that kind.
      const result = await generateDefinition({ variables: { organisationId, prompt, kind: AI_KIND[kind], image: reference.image, progressChannel } }).finally(() => progress.end());
      const generated = result.data?.objectDefinitionGenerate;
      const parsed = parseObjectDefinitionSpec(generated?.spec);
      if (!generated || !('spec' in parsed)) {
        setAiError('AI_INVALID_SPEC');
        return;
      }
      spec = parsed.spec;
      suggestedName = generated.name ?? null;
    } catch (e) {
      setAiError(aiErrorCode(e));
      return;
    }
    const finalName = (name.trim() || suggestedName || prompt).slice(0, 80);
    if (!name.trim()) setName(finalName);
    try {
      // Designed by the AI: a smart object.
      const result = await createDefinition({ variables: { organisationId, name: finalName, categoryId, spec, smart: true } });
      const id = result.data?.objectDefinitionCreate.id;
      if (id) onCreated(id);
    } catch {
      /* shown inline from the mutation's error state */
    }
  }, [description, generateDefinition, organisationId, kind, reference.image, name, createDefinition, categoryId, onCreated, progress]);

  // Dimensions always start from a style (no AI).
  const describing = mode === 'describe' && kind !== 'DIMENSION';
  const pickKind = (next: NewKind) => {
    setKind(next);
    setStarterId(BLANK);
    setCategoryId(next === 'OBJECT' ? SketchObjectCategory.FURNITURE : next === 'DIMENSION' ? DIMENSION_CATEGORY_ID : OPENING_CATEGORY_IDS[next]);
    setStep('details');
  };
  const submit = describing ? generateAndCreate : create;
  const canSubmit = describing ? description.trim() !== '' && !reference.reading : name.trim() !== '';

  const fields = (
    <div className="flex flex-col gap-4">
      <div className="grid grid-cols-2 gap-4">
        <label className="flex flex-col gap-1 text-sm">
          <T _str="name" swc />
          <Input value={name} onChange={(e) => setName(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && canSubmit && void submit()} placeholder={namePlaceholder} maxLength={80} autoFocus />
        </label>
        {kind === 'OBJECT' ? (
          <label className="flex flex-col gap-1 text-sm">
            <T _str="category" swc />
            <Select anchored value={categoryId} onChange={(e) => setCategoryId(e.target.value)} renderSelectedValue={(id) => <TranslatedLabel label={categories.find((c) => c.id === id)?.label ?? id} swc />}>
              {categories.map((c) => (
                <Option key={c.id} value={c.id}>
                  <TranslatedLabel label={c.label} swc />
                </Option>
              ))}
            </Select>
          </label>
        ) : (
          <div className="flex flex-col gap-1 text-sm">
            <T _str="category" swc />
            {/* Fixed: doors and windows appear in the door / window type dropdowns, dimensions in the dimension tool. */}
            <div className="flex h-9 items-center text-gray-500 dark:text-gray-400">
              <TranslatedLabel label={kind === 'DIMENSION' ? DIMENSION_CATEGORY_LABEL : OPENING_CATEGORY_LABELS[kind]} swc />
            </div>
          </div>
        )}
      </div>
    </div>
  );

  if (step === 'kind')
    return (
      <Modal.Container open={open} onClose={onClose} panelClassName="flex max-h-[calc(100vh-2rem)] max-w-3xl flex-col">
        <Modal.Header onClose={onClose} className="!mb-5 shrink-0">
          <T _str="what are you making?" swc />
        </Modal.Header>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 md:grid-cols-4">
          {KIND_CHOICES.map((choice) => (
            <button
              key={choice.kind}
              type="button"
              onClick={() => pickKind(choice.kind)}
              className="flex flex-col items-center gap-3 rounded-xl border border-gray-200 p-5 text-center transition-colors hover:border-black hover:bg-neon-green-300/20 dark:border-gray-600 dark:hover:border-white"
            >
              <choice.icon className="h-12 w-12 text-gray-700 dark:text-gray-200" />
              <span className="font-medium">{choice.label}</span>
              <span className="text-xs leading-snug text-gray-500 dark:text-gray-400">{choice.description}</span>
            </button>
          ))}
        </div>
      </Modal.Container>
    );

  return (
    // Fixed header + footer; only the starting points scroll when the window is short.
    <Modal.Container open={open} onClose={onClose} panelClassName="flex max-h-[calc(100vh-2rem)] max-w-3xl flex-col">
      <Modal.Header onClose={onClose} className="!mb-4 shrink-0">
        <span className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setStep('kind')}
            disabled={loading}
            aria-label={backLabel}
            title={backLabel}
            className="rounded p-0.5 text-gray-500 transition-colors hover:text-black disabled:opacity-40 dark:text-gray-400 dark:hover:text-white"
          >
            <ChevronLeftIcon className="h-5 w-5" />
          </button>
          {kind === 'DOOR' ? <T _str="new door" swc /> : kind === 'WINDOW' ? <T _str="new window" swc /> : kind === 'DIMENSION' ? <T _str="new dimension" swc /> : <T _str="new object" swc />}
        </span>
      </Modal.Header>
      <div className="shrink-0">{fields}</div>
      <div className="mt-5 mb-2 flex shrink-0 items-center gap-3">
        <span className="text-sm font-medium">
          <T _str="start from" swc />
        </span>
        <span role="radiogroup" className={classNames('flex overflow-hidden rounded-lg border border-gray-300 dark:border-gray-600', kind === 'DIMENSION' && 'hidden')}>
          {(
            [
              { value: 'describe', icon: SparklesIcon, label: <T _str="smart object" swc /> },
              { value: 'template', icon: Squares2X2Icon, label: <T _str="blank or template" swc /> },
            ] satisfies ReadonlyArray<{ value: StartMode; icon: typeof SparklesIcon; label: React.ReactNode }>
          ).map((option) => (
            <button
              key={option.value}
              type="button"
              role="radio"
              aria-checked={mode === option.value}
              disabled={loading}
              onClick={() => setMode(option.value)}
              className={classNames(
                'flex items-center gap-1.5 px-3 py-1 text-sm transition-colors disabled:cursor-not-allowed',
                mode === option.value ? 'bg-neon-green-300 font-medium text-black' : 'bg-white hover:bg-gray-100 dark:bg-[#262626] dark:hover:bg-black',
              )}
            >
              <option.icon className="h-4 w-4" />
              {option.label}
            </button>
          ))}
        </span>
      </div>
      <Modal.Body className="-mx-1 min-h-0 flex-1 overflow-y-auto px-1 pb-1">
        {describing ? (
          <div className="flex flex-col gap-3">
            <label className="flex flex-col gap-1 text-sm">
              <T _str="what should the object look like?" swc />
              <TextArea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                onPaste={reference.onPaste}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && (e.ctrlKey || e.metaKey) && canSubmit && !loading) {
                    e.preventDefault();
                    void submit();
                  }
                }}
                placeholder={descriptionPlaceholders[kind]}
                maxLength={AI_MAX_PROMPT_LENGTH}
                disabled={loading}
                className="min-h-[6rem] text-sm"
              />
            </label>
            <ReferenceImageField state={reference} disabled={loading} />
            {run.running ? (
              // The thoughts go to the message log below (in place of the buttons).
              <AiProgress run={run} hideThought />
            ) : (
              <p className="text-xs text-gray-500 dark:text-gray-400">
                <T _str="describe it and a smart object is designed for you. You change it later the same way. Leave the name empty to use the suggested name." swc />
              </p>
            )}
            {aiError && <AiErrorMessage code={aiError} />}
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
            {starters.map((s) => (
              <button
                key={s.id}
                type="button"
                onClick={() => pickStarter(s)}
                title={s.id === BLANK ? undefined : s.description}
                className={classNames(
                  'flex flex-col gap-1.5 rounded-xl border p-2.5 text-left transition-colors',
                  s.id === starterId ? 'border-black bg-neon-green-300/20 dark:border-white' : 'border-gray-200 hover:border-gray-400 dark:border-gray-600 dark:hover:border-gray-400',
                )}
              >
                <div className="h-12 w-full text-gray-700 dark:text-gray-200">{s.id === BLANK ? <PlusIcon className="mx-auto h-8 w-8 opacity-60" /> : <GlyphPreview segments={previews.get(s.id) ?? []} />}</div>
                <div className="text-sm font-medium leading-tight">
                  <TranslatedLabel label={s.label} swc />
                </div>
                <div className="line-clamp-2 text-xs leading-snug text-gray-500 dark:text-gray-400">
                  <TranslatedLabel label={s.description} swc />
                </div>
              </button>
            ))}
          </div>
        )}
      </Modal.Body>
      {error && (
        <p role="alert" className="mt-3 shrink-0 rounded-lg bg-error-red-500/10 px-3 py-2 text-sm text-error-red-500">
          <T _str="the object could not be created" swc />
        </p>
      )}
      {describing && run.running ? (
        // While the AI works, its messages come in here (read-only) instead of the buttons.
        <AiMessageLog run={run} className="mt-4 shrink-0" />
      ) : (
        <Modal.Footer className="mt-4 shrink-0 justify-end gap-2">
          <Button variant="secondary" size="xs" onClick={onClose}>
            <T _str="cancel" swc />
          </Button>
          <Button variant="primary" size="xs" onClick={submit} loading={loading} disabled={!canSubmit}>
            {describing ? <T _str="generate and open editor" swc /> : <T _str="create and open editor" swc />}
          </Button>
        </Modal.Footer>
      )}
    </Modal.Container>
  );
};

export const NewObjectModal = memo(NewObjectModal_);
